import { assets as assetsNs, contract, core, formatDay, goals as goalsNs, parseDay, plan as planNs } from '@map-my-money/engine';
import { linePoints, monthEndPoint } from './line.ts';
import { definitionsFor } from './definitions.ts';
import { goalView } from './goals.ts';
import { money, percentChange } from './money.ts';
import { mortgageFreeDate } from './mortgage.ts';
import { nowState } from './now.ts';
import { anchorOf, runForecast, type Forecasted, type ViewInput } from './run.ts';

/** Assets owned today: bought, active, not sold. */
export function ownedAssets(f: Pick<Forecasted, 'plan' | 'today'>) {
  return f.plan.assets
    .filter((a) => a.isActive && !planNs.isSoldBy(a.saleEvent, f.today) && a.purchaseDate <= f.today)
    .map((a) => {
      const value = assetsNs.assetValue(a, f.today);
      return {
        id: a.id,
        label: a.label,
        asset_class: a.assetClass,
        value: money(value),
        purchase_price: money(a.purchasePrice),
        change_since_bought_percent: percentChange(a.purchasePrice, value),
      };
    });
}

/** The financial position today, as Home and get_overview show it. */
export function overview(input: ViewInput) {
  const f = runForecast(input);
  const now = nowState(f);
  const end = monthEndPoint(linePoints(f));
  const star = goalsNs.northStar(f.goals);
  const latest = anchorOf(input.waypoints, input.asOf);
  const setupDay = planNs.balanceDate(f.plan.configuration, core.startOfMonth(f.today));
  const debtFree = f.forecast.metrics.debtFreeDate;
  const mortgageFree = mortgageFreeDate(f.plan, f.today);
  return {
    as_of: input.asOf,
    net_worth: money(now.netWorth),
    breakdown: {
      cash: money(now.cash),
      investments: money(now.investments),
      property_equity: money(now.propertyEquity),
      other_assets: money(now.assetValue),
      debt: money(now.debt),
    },
    /** The end of this month: now plus everything still to come in it (R21), the line's point for this month. */
    month_end: {
      date: formatDay(end.date),
      net_worth_estimate: money(end.netWorth),
      change_rest_of_month: money(core.sub(end.netWorth, now.netWorth)),
      cash_change: money(core.sub(end.cash, now.cash)),
      investment_growth: money(core.sub(end.investments, now.investments)),
      cash: money(end.cash),
      investments: money(end.investments),
    },
    north_star: star ? goalView(star, f.today) : null,
    goals: f.goals.map((g) => goalView(g, f.today)),
    what_you_own: ownedAssets(f),
    latest_waypoint: latest
      ? {
          date: latest.date,
          days_ago: core.daysBetween(parseDay(latest.date), f.today),
          net_worth: money(planNs.snapshotNetWorth(planNs.resolvingAssetValue(contract.decodeBalances(latest.balances), f.plan, parseDay(latest.date)))),
          cash: latest.balances.cash ?? '0',
          investments: latest.balances.investments ?? '0',
          source: latest.source ?? 'unknown',
        }
      : null,
    now_anchor: now.anchor === null ? 'plan_start' as const : 'waypoint' as const,
    /** What now starts from: the latest check-in, or the setup balances as an estimate from the day they were entered (decision 7). */
    anchor: latest ? { kind: 'check-in' as const, date: latest.date } : { kind: 'estimate' as const, date: formatDay(setupDay) },
    debt_free_date: debtFree === null ? null : formatDay(debtFree),
    mortgage_free_date: mortgageFree === null ? null : formatDay(mortgageFree),
    definitions: definitionsFor('now', 'net_worth', 'waypoint', 'month', 'debt_free', 'mortgage_free', 'goal_progress', 'goals_horizon', 'projection'),
  };
}

export type OverviewResult = ReturnType<typeof overview>;
